import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Globe, CheckCircle2, Lock, Loader2, Star, ArrowLeft,
    Crown, Medal, Trophy, Users, RefreshCw,
    ChevronDown, ChevronUp, Target, AlertTriangle, Flag, ShieldOff, Eye,
    Share2, Copy, Check, Scale,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl } from '../lib/assetUrls';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import LastManStandingCompetitionPage from './LastManStandingCompetitionPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function useCutoffCountdown(kickoffIso) {
    const cutoff = kickoffIso ? new Date(new Date(kickoffIso).getTime() - 30 * 60 * 1000) : null;
    const [now, setNow] = useState(() => Date.now());
    const timerRef = useRef(null);

    useEffect(() => {
        if (!cutoff) return;
        const msToStart = cutoff.getTime() - 2 * 60 * 60 * 1000 - Date.now();
        const startTicking = () => {
            timerRef.current = setInterval(() => setNow(Date.now()), 1000);
        };
        if (msToStart <= 0) {
            startTicking();
        } else {
            const delay = setTimeout(startTicking, msToStart);
            return () => { clearTimeout(delay); clearInterval(timerRef.current); };
        }
        return () => clearInterval(timerRef.current);
    }, [kickoffIso]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!cutoff) return { isPastCutoff: false, label: null, isUrgent: false, isImminent: false };

    const msLeft = cutoff.getTime() - now;
    const isPastCutoff = msLeft <= 0;
    const withinTwoHours = msLeft <= 2 * 60 * 60 * 1000;

    if (!withinTwoHours && !isPastCutoff) return { isPastCutoff: false, label: null, isUrgent: false, isImminent: false };
    if (isPastCutoff) return { isPastCutoff: true, label: 'Predictions closed', isUrgent: true, isImminent: true };

    const totalSecs = Math.floor(msLeft / 1000);
    const hrs  = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    const label = hrs > 0
        ? `Closes in ${hrs}h ${mins}m`
        : mins > 0
            ? `Closes in ${mins}m ${String(secs).padStart(2, '0')}s`
            : `Closes in ${secs}s`;

    return { isPastCutoff: false, label, isUrgent: msLeft < 60 * 60 * 1000, isImminent: msLeft < 15 * 60 * 1000 };
}

function formatKickoff(isoString) {
    if (!isoString) return '';
    try { return format(parseISO(isoString), 'HH:mm'); } catch { return isoString; }
}

function formatMatchDate(yyyymmdd) {
    if (!yyyymmdd || yyyymmdd.length < 8) return yyyymmdd;
    try {
        const d = parseISO(`${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}`);
        return format(d, 'EEE d MMM');
    } catch { return yyyymmdd; }
}

function formatCompDate(yyyymmdd) {
    if (!yyyymmdd || yyyymmdd.length < 8) return '';
    try {
        const d = parseISO(`${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}`);
        return format(d, 'EEEE d MMMM yyyy');
    } catch { return yyyymmdd; }
}

function statusBadge(status) {
    if (status === 'live') return <Badge className="bg-green-500 text-white animate-pulse">Live</Badge>;
    if (status === 'finished') return <Badge variant="secondary">FT</Badge>;
    if (status === 'postponed') return <Badge variant="destructive">PPD</Badge>;
    return null;
}

function initials(name) {
    return (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function TeamBadge({ teamId, teamName, size = 'md' }) {
    const [error, setError] = useState(false);
    const sz = size === 'sm' ? 'w-6 h-6' : 'w-10 h-10';
    if (!teamId || error) {
        return (
            <div className={`${sz} rounded-full bg-muted flex items-center justify-center flex-shrink-0`}>
                <span className="text-xs font-bold text-muted-foreground">
                    {(teamName || '?')[0].toUpperCase()}
                </span>
            </div>
        );
    }
    return (
        <img
            src={badgeUrl(teamId)}
            alt={teamName}
            className={`${sz} object-contain flex-shrink-0`}
            onError={() => setError(true)}
        />
    );
}

// ── Team Intel tooltip (FIFA ranking snapshot + recent form) ─────────────────
const _intelCache = {};

function TeamIntelTooltip({ teamId, teamName, children }) {
    const [intel, setIntel] = useState(_intelCache[teamId] || null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const fetchIntel = async () => {
        if (!teamId || intel || loading || _intelCache[teamId] === null) return;
        if (_intelCache[teamId]) { setIntel(_intelCache[teamId]); return; }
        setLoading(true);
        try {
            const res = await axios.get(
                `${API_URL}/api/football/worldcup/team-intel/${teamId}`,
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            _intelCache[teamId] = res.data;
            setIntel(res.data);
        } catch {
            _intelCache[teamId] = null;
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = () => { fetchIntel(); setOpen(true); };

    const rankDiff = intel ? (intel.previous_rank - intel.rank) : 0;

    return (
        <Tooltip open={open} onOpenChange={setOpen}>
                <TooltipTrigger
                    asChild
                    onMouseEnter={handleOpen}
                    onFocus={handleOpen}
                    onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : handleOpen(); }}
                >
                    <span className="cursor-pointer">{children}</span>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-card text-foreground border border-border shadow-xl text-xs p-3 rounded-xl z-50">
                    <div className="min-w-[200px] space-y-2.5">
                        {/* Header */}
                        <div className="flex items-center gap-2">
                            <TeamBadge teamId={teamId} teamName={teamName} size="sm" />
                            <div>
                                <p className="font-bold text-sm leading-tight">{intel?.pa_team_name || teamName}</p>
                                <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{intel?.confederation || ''}</p>
                            </div>
                        </div>

                        {loading && <p className="text-xs text-muted-foreground">Loading...</p>}

                        {intel && (
                            <>
                                {/* Rank */}
                                <div className="flex items-center justify-between border-t border-border/50 pt-2">
                                    <span className="text-xs text-muted-foreground">FIFA Ranking</span>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-lg font-black leading-none">#{intel.rank}</span>
                                        {rankDiff !== 0 && (
                                            <span className={`text-[10px] font-bold ${rankDiff > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                {rankDiff > 0 ? `▲${rankDiff}` : `▼${Math.abs(rankDiff)}`}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Recent form */}
                                {intel.recent_matches?.length > 0 && (
                                    <div className="border-t border-border/50 pt-2 space-y-1">
                                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">Recent Results</p>
                                        {intel.recent_matches.slice(0, 5).map((m, i) => {
                                            const isHome = m.home_code === intel.country_code;
                                            const teamScore = isHome ? m.home_score : m.away_score;
                                            const oppScore  = isHome ? m.away_score : m.home_score;
                                            const oppName   = isHome ? m.away : m.home;
                                            const result    = teamScore > oppScore ? 'W' : teamScore < oppScore ? 'L' : 'D';
                                            const col       = result === 'W' ? 'text-green-400' : result === 'L' ? 'text-red-400' : 'text-amber-400';
                                            return (
                                                <div key={i} className="flex items-center justify-between gap-2 text-xs">
                                                    <div className="flex items-center gap-1 min-w-0">
                                                        <span className={`font-black w-4 flex-shrink-0 ${col}`}>{result}</span>
                                                        <span className="text-muted-foreground truncate">{oppName}</span>
                                                    </div>
                                                    <span className="font-bold tabular-nums flex-shrink-0">{teamScore}–{oppScore}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <p className="text-[9px] font-semibold text-muted-foreground/60 uppercase tracking-widest border-t border-border/30 pt-1.5">
                                    Pre-tournament data
                                </p>
                            </>
                        )}

                        {!loading && !intel && (
                            <p className="text-xs text-muted-foreground border-t border-border/50 pt-2">No data available</p>
                        )}
                    </div>
                </TooltipContent>
            </Tooltip>
    );
}

function RankBadge({ rank }) {
    if (rank === 1) return (
        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-amber-500/20 flex-shrink-0">
            <Crown className="h-4 w-4 text-amber-400" />
        </div>
    );
    if (rank === 2) return (
        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-slate-400/20 flex-shrink-0">
            <Medal className="h-4 w-4 text-slate-400" />
        </div>
    );
    if (rank === 3) return (
        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-amber-700/20 flex-shrink-0">
            <Medal className="h-4 w-4 text-amber-600" />
        </div>
    );
    return (
        <div className="flex items-center justify-center w-7 h-7 flex-shrink-0">
            <span className="text-sm font-bold text-muted-foreground">{rank}</span>
        </div>
    );
}

// ── Score stepper ─────────────────────────────────────────────────────────────

function ScoreStepper({ value, onChange }) {
    return (
        <div className="flex flex-col items-center gap-0.5">
            <button
                onClick={() => onChange(Math.min(99, (parseInt(value, 10) || 0) + 1))}
                className="w-8 h-7 text-muted-foreground hover:text-foreground font-bold text-lg leading-none flex items-center justify-center transition-colors"
            >+</button>
            <span className="text-2xl font-bold tabular-nums w-8 text-center">
                {value === '' ? '–' : value}
            </span>
            <button
                onClick={() => onChange(Math.max(0, (parseInt(value, 10) || 0) - 1))}
                className="w-8 h-7 text-muted-foreground hover:text-foreground font-bold text-lg leading-none flex items-center justify-center transition-colors"
            >–</button>
        </div>
    );
}

// ── Lock All Bar ──────────────────────────────────────────────────────────────

function LockAllBar({ pendingPicks, onLockAll, submitting }) {
    const [open, setOpen] = useState(false);

    if (pendingPicks.length < 2) return null;

    return (
        <>
            {/* Review & lock modal */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Lock className="h-4 w-4 text-amber-500" />
                            Review & Lock All Predictions
                        </DialogTitle>
                        <DialogDescription>
                            {pendingPicks.length} predictions will be <strong>permanently locked</strong>. You won't be able to change these again.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 my-1 max-h-72 overflow-y-auto pr-1">
                        {pendingPicks.map(({ match, home, away }) => (
                            <div key={match.pa_match_id} className="flex items-center gap-2 bg-muted/40 rounded-lg px-3 py-2.5 text-sm">
                                <span className="truncate flex-1 text-right font-medium">{match.home_team_name}</span>
                                <span className="font-bold tabular-nums bg-background border border-border rounded px-2 py-0.5 text-sm flex-shrink-0">
                                    {home} – {away}
                                </span>
                                <span className="truncate flex-1 font-medium">{match.away_team_name}</span>
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button
                            className="bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={() => { onLockAll(pendingPicks); setOpen(false); }}
                            disabled={submitting}
                        >
                            {submitting
                                ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                : <Lock className="h-4 w-4 mr-2" />}
                            Lock All {pendingPicks.length}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Sticky bar */}
            <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-amber-500/40 bg-background/95 backdrop-blur-sm shadow-2xl">
                <div className="container-app flex items-center justify-between gap-4 px-4 py-3">
                    <div>
                        <p className="font-semibold text-sm">
                            {pendingPicks.length} prediction{pendingPicks.length !== 1 ? 's' : ''} ready to lock
                        </p>
                        <p className="text-xs text-muted-foreground">Review your picks before permanently locking them in</p>
                    </div>
                    <Button
                        className="bg-amber-500 hover:bg-amber-600 text-white flex-shrink-0"
                        onClick={() => setOpen(true)}
                        disabled={submitting}
                    >
                        {submitting
                            ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            : <Lock className="h-4 w-4 mr-2" />}
                        Review & Lock All
                    </Button>
                </div>
            </div>
        </>
    );
}

// ── Match Card ────────────────────────────────────────────────────────────────

function MatchCard({ match, prediction, onSubmit, submitting, lockReason, spectator = false, bonusQuestions = [], bonusAnswers = {}, onBonusAnswer, onScoreChange }) {
    const { isPastCutoff, label: cutoffLabel, isUrgent, isImminent } = useCutoffCountdown(match.kickoff_time);
    const matchLocked = match.status !== 'scheduled' || isPastCutoff;

    // For knockout ("FT only") matches in extra time / penalties, treat the
    // match as finished for predictor display purposes — lock the score at 90 min.
    // Official PA ET/penalty codes + short-form codes seen in production
    // FTET = Full Time, Extra Time to be Played (FT→ET transition)
    // ETS  = Extra Time has Started
    // ETFTPT = ET Full Time, Penalties Being Played
    // PT   = Penalty Shoot Out has Started
    // PTFT = Penalty Shoot Out has Ended
    const _ET_PHASES = new Set([
        'FTET', 'ETS',
        'ETFHK','ETFHS','ETFHE','ETHT','ETSHK','ETSHS','ETSHE',
        'ETFTPT','AET',
        'PT','PTFT','PSO','PEN',
        // Short-form seen in production
        'EFHK','EFHS','EH','ESHK','ESHS','E1','E2',
    ]);
    const _r = (match.round || '').toLowerCase();
    const _isKnockout = _r.includes('final') || _r.includes('semi') || _r.includes('quarter') || _r.includes('round of') || _r.includes('third') || _r.includes('play-off');
    const _phase = (match.match_phase || '').toUpperCase();
    const _ftPhases = new Set(['FT', 'PTMT', 'FTET']);  // 90-min full time / ET about to start
    const _inET = _ET_PHASES.has(_phase) || match.is_extra_time;
    // Lock a knockout's display at the 90-min result once: it's in ET, at 90-min FT,
    // or the backend has already captured the 90-min score (ft_home_score). Any of
    // these means the predictor result is decided even though play may continue.
    const _ftLocked = _isKnockout && (_inET || _ftPhases.has(_phase) || match.ft_home_score != null);

    const isFinished = match.status === 'finished' || _ftLocked;
    const isLive = match.status === 'live' && !_ftLocked;

    // For FT-only knockout matches in ET, use the captured FT score if available
    const displayHomeScore = (_ftLocked && match.ft_home_score != null) ? match.ft_home_score : match.home_score;
    const displayAwayScore = (_ftLocked && match.ft_away_score != null) ? match.ft_away_score : match.away_score;
    const predLocked = prediction?.locked === true;
    const [home, setHome] = useState('0');
    const [away, setAway] = useState('0');
    const [showWarning, setShowWarning] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (prediction) {
            setHome(String(prediction.predicted_home_score));
            setAway(String(prediction.predicted_away_score));
        }
    }, [prediction]);

    const handleConfirmLock = async () => {
        if (home === '' || away === '') {
            toast.error('Please enter a score prediction for both teams before locking in.');
            setShowWarning(false);
            return;
        }
        const h = parseInt(home, 10);
        const a = parseInt(away, 10);
        await onSubmit(match.pa_match_id, h, a, true);
        setShowWarning(false);
        setIsEditing(false);
    };

    const pointsEarned = prediction?.points_awarded ?? null;
    // Show stepper only when entering for first time or explicitly editing
    // Spectators (eliminated players) see everything read-only — no inputs, no buttons
    const showStepper = !spectator && !matchLocked && !predLocked && (isEditing || !prediction);

    // Lock In is blocked until every bonus question has an answer
    const allBonusAnswered = bonusQuestions.length === 0 || bonusQuestions.every(q => {
        const val = bonusAnswers[q.id];
        return val !== '' && val !== null && val !== undefined;
    });
    const unansweredCount = bonusQuestions.filter(q => {
        const val = bonusAnswers[q.id];
        return val === '' || val === null || val === undefined;
    }).length;

    const statusLabel = isLive ? 'LIVE' : isFinished ? 'FULL TIME' : 'NOT STARTED';
    const statusColour = isLive ? 'text-green-400' : isFinished ? 'text-muted-foreground' : 'text-muted-foreground';

    return (
        <>
        {/* Lock warning dialog */}
        <Dialog open={showWarning} onOpenChange={setShowWarning}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-400" />
                        Final Change Warning
                    </DialogTitle>
                    <DialogDescription>
                        {prediction
                            ? <>This is your <strong>one allowed change</strong>. Once you confirm, your prediction will be <strong>permanently locked</strong> and cannot be edited again.</>
                            : <>Once you confirm, your prediction will be <strong>permanently locked</strong> and cannot be edited again.</>
                        }
                    </DialogDescription>
                </DialogHeader>
                <div className="flex items-center justify-center gap-4 py-3">
                    <div className="text-center">
                        <p className="text-xs text-muted-foreground mb-1">{match.home_team_name}</p>
                        <span className="text-2xl font-bold tabular-nums">{home}</span>
                    </div>
                    <span className="text-muted-foreground font-bold">v</span>
                    <div className="text-center">
                        <p className="text-xs text-muted-foreground mb-1">{match.away_team_name}</p>
                        <span className="text-2xl font-bold tabular-nums">{away}</span>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setShowWarning(false)}>Cancel</Button>
                    <Button
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                        onClick={handleConfirmLock}
                        disabled={submitting === match.pa_match_id}
                    >
                        {submitting === match.pa_match_id
                            ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            : <Lock className="h-4 w-4 mr-2" />}
                        Confirm & Lock
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        <Card className="overflow-hidden relative">
            {/* Round-locked overlay */}
            {lockReason && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background/80 backdrop-blur-sm rounded-lg px-4 text-center">
                    <Lock className="h-5 w-5 text-muted-foreground" />
                    <p className="text-sm font-medium text-muted-foreground">{lockReason}</p>
                </div>
            )}
            {/* Card header: date + kickoff + status */}
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {formatMatchDate(match.match_date)}
                    {/* Show round name if available */}
                    {match.round && <span className="ml-1.5 text-primary/70">{match.round}</span>}
                </span>
                <div className="flex items-center gap-2">
                    {isLive && <span className="flex h-2 w-2 rounded-full bg-green-400 animate-pulse" />}
                    <span className="text-xs text-muted-foreground">
                        {formatKickoff(match.kickoff_time)}
                    </span>
                    {cutoffLabel && (
                        <span className={`text-xs font-semibold tabular-nums ${
                            isImminent ? 'text-red-400' : isUrgent ? 'text-amber-400' : 'text-muted-foreground'
                        }`}>
                            · {cutoffLabel}
                        </span>
                    )}
                    {match.status === 'postponed' && <Badge variant="destructive" className="text-xs">PPD</Badge>}
                    {/* Knockout warning pill */}
                    {(() => {
                        const r = (match.round || '').toLowerCase();
                        const isKnockout = r.includes('final') || r.includes('semi') || r.includes('quarter') || r.includes('round of') || r.includes('third');
                        return isKnockout ? (
                            <span title="Scored on 90-minute result only. Extra time & penalties don't count." className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 border border-amber-500/30 font-medium cursor-help">
                                ⏱ FT only
                            </span>
                        ) : null;
                    })()}
                </div>
            </div>

            <CardContent className="px-4 pb-4 pt-1">
                {/* Teams row */}
                <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex flex-col items-center gap-1 w-16">
                        <TeamIntelTooltip teamId={match.home_team_id} teamName={match.home_team_name}>
                            <TeamBadge teamId={match.home_team_id} teamName={match.home_team_name} />
                        </TeamIntelTooltip>
                        <p className="text-xs font-semibold text-center leading-tight">{match.home_team_name}</p>
                    </div>

                    {/* Score area */}
                    <div className="flex-1 flex flex-col items-center gap-2">
                        {/* YOU PREDICTED row */}
                        <div className="w-full flex flex-col items-center gap-0.5">
                            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                You Predicted
                            </span>
                            {showStepper ? (
                                <div className="flex items-center gap-2">
                                    <ScoreStepper value={home} onChange={v => { setHome(String(v)); onScoreChange?.(match.pa_match_id, v, parseInt(away, 10)); }} />
                                    <span className="text-muted-foreground font-bold">v</span>
                                    <ScoreStepper value={away} onChange={v => { setAway(String(v)); onScoreChange?.(match.pa_match_id, parseInt(home, 10), v); }} />
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <span className="text-2xl font-bold tabular-nums w-8 text-center">
                                        {prediction ? prediction.predicted_home_score : '–'}
                                    </span>
                                    <span className="text-muted-foreground font-bold">v</span>
                                    <span className="text-2xl font-bold tabular-nums w-8 text-center">
                                        {prediction ? prediction.predicted_away_score : '–'}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Divider + status label */}
                        <div className="w-full flex items-center gap-2">
                            <div className="flex-1 h-px bg-border" />
                            <span className={`text-xs font-bold uppercase tracking-widest ${statusColour}`}>
                                {statusLabel}
                            </span>
                            <div className="flex-1 h-px bg-border" />
                        </div>

                        {/* Live / actual score row */}
                        <div className="flex items-center gap-2">
                            <span className={`text-2xl font-bold tabular-nums w-8 text-center ${isLive ? 'text-green-400' : ''}`}>
                                {(isLive || isFinished) ? (displayHomeScore != null ? displayHomeScore : '–') : '–'}
                            </span>
                            <span className="text-muted-foreground font-bold">v</span>
                            <span className={`text-2xl font-bold tabular-nums w-8 text-center ${isLive ? 'text-green-400' : ''}`}>
                                {(isLive || isFinished) ? (displayAwayScore != null ? displayAwayScore : '–') : '–'}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-col items-center gap-1 w-16">
                        <TeamIntelTooltip teamId={match.away_team_id} teamName={match.away_team_name}>
                            <TeamBadge teamId={match.away_team_id} teamName={match.away_team_name} />
                        </TeamIntelTooltip>
                        <p className="text-xs font-semibold text-center leading-tight">{match.away_team_name}</p>
                    </div>
                </div>

                {/* Venue */}
                {match.venue && (
                    <p className="text-xs text-muted-foreground text-center pb-2">{match.venue}</p>
                )}

                {/* Footer: points + action button */}
                <div className="flex items-center justify-between pt-2 border-t gap-2 min-h-[36px]">
                    <div className="flex items-center gap-2">
                        {pointsEarned !== null && (
                            <Badge
                                variant={pointsEarned > 0 ? 'default' : 'secondary'}
                                className={pointsEarned > 0 ? 'bg-amber-500 text-white' : ''}
                            >
                                {pointsEarned > 0 ? `+${pointsEarned} pts` : '0 pts'}
                            </Badge>
                        )}
                        {predLocked && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Lock className="h-3 w-3" /> Locked
                            </span>
                        )}
                        {spectator && (
                            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted/60 border border-border rounded-full px-2.5 py-1">
                                <Eye className="h-3 w-3" /> Spectating
                            </span>
                        )}
                        {!spectator && !matchLocked && !prediction && !isEditing && (
                            <span className="text-xs text-muted-foreground">No prediction yet</span>
                        )}
                        {!spectator && !matchLocked && prediction && !predLocked && !isEditing && (
                            <span className="text-xs text-green-500">Prediction saved</span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {/* Change button: prediction saved but not locked */}
                        {!spectator && !matchLocked && prediction && !predLocked && !isEditing && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setIsEditing(true)}
                            >
                                Change
                            </Button>
                        )}
                        {/* Cancel editing */}
                        {isEditing && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    setIsEditing(false);
                                    if (prediction) {
                                        setHome(String(prediction.predicted_home_score));
                                        setAway(String(prediction.predicted_away_score));
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                        {showStepper && (
                            <div className="flex flex-col items-end gap-1">
                                {!allBonusAnswered && (
                                    <p className="text-[10px] text-amber-500 text-right">
                                        Answer {unansweredCount === 1 ? 'the question' : `all ${unansweredCount} questions`} below first
                                    </p>
                                )}
                                <Button
                                    size="sm"
                                    className="bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
                                    onClick={() => setShowWarning(true)}
                                    disabled={submitting === match.pa_match_id || !allBonusAnswered}
                                    title={!allBonusAnswered ? 'Answer all bonus questions before locking in' : undefined}
                                >
                                    {submitting === match.pa_match_id
                                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        : <><Lock className="h-3.5 w-3.5 mr-1.5" />Lock In</>}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
                {/* Bonus questions */}
                {bonusQuestions.length > 0 && !matchLocked && (
                    <div className="mt-3 pt-3 border-t space-y-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bonus Questions</p>
                        {bonusQuestions.map(q => {
                            const current = bonusAnswers[q.id] ?? '';
                            const isLocked = predLocked || spectator;
                            const unanswered = !isLocked && (current === '' || current === null || current === undefined);
                            return (
                                <div key={q.id} className="space-y-1.5">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5">
                                            {unanswered && <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />}
                                            <p className={`text-sm font-medium ${unanswered ? 'text-red-400' : ''}`}>{q.label}</p>
                                        </div>
                                        <span className="text-xs text-muted-foreground">+{q.points} pt{q.points !== 1 ? 's' : ''}{q.tiebreaker ? ' 🎯' : ''}</span>
                                    </div>
                                    {q.type === 'number' ? (
                                        <input
                                            type="number"
                                            min={q.min ?? 0}
                                            max={q.max ?? 20}
                                            value={current}
                                            disabled={isLocked}
                                            onChange={e => onBonusAnswer && onBonusAnswer(q.id, e.target.value)}
                                            className="w-20 h-8 text-center border rounded-md text-sm bg-background disabled:opacity-50 disabled:cursor-not-allowed"
                                            placeholder="0"
                                        />
                                    ) : (
                                        <div className="flex flex-wrap gap-1.5">
                                            {(q.options || []).map(opt => (
                                                <button
                                                    key={opt.value}
                                                    disabled={isLocked}
                                                    onClick={() => !isLocked && onBonusAnswer && onBonusAnswer(q.id, opt.value)}
                                                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                        current === opt.value
                                                            ? 'bg-primary text-primary-foreground border-primary'
                                                            : 'border-border hover:border-primary/50 hover:bg-muted/60'
                                                    }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {/* Show settled bonus answers when match is finished */}
                {isFinished && prediction?.bonus_breakdown && Object.keys(prediction.bonus_breakdown).length > 0 && (
                    <div className="mt-3 pt-3 border-t space-y-1.5">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bonus Results</p>
                        {bonusQuestions.map(q => {
                            const pts = prediction.bonus_breakdown?.[q.id] ?? null;
                            const predicted = prediction.bonus_answers?.[q.id];
                            const correct = prediction.correct_bonus_answers?.[q.id];
                            const isClosest = q.scoring === 'closest';
                            const isVoided = correct === 'no_goal' || correct === null || correct === undefined;
                            const opt = (q.options || []).find(o => o.value === predicted);
                            const correctOpt = (q.options || []).find(o => o.value === correct);
                            const predictedLabel = opt?.label || predicted || '–';
                            const correctLabel = correct === 'no_goal' ? 'no goal scored' : (correctOpt?.label || correct);
                            // For closest questions: only show "was X" if user got 0 pts (not closest)
                            const showCorrection = !isVoided && correct && correct !== predicted && (!isClosest || pts === 0);
                            if (isVoided && pts === null) return null; // voided questions not in breakdown
                            return (
                                <div key={q.id} className="text-xs">
                                    <p className="text-muted-foreground leading-snug mb-1">{q.label}</p>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className={pts > 0 ? 'text-green-500 font-medium' : 'text-muted-foreground'}>
                                            {predictedLabel}{showCorrection ? ` (was ${correctLabel})` : ''}
                                            {isClosest && pts > 0 && correct && correct !== predicted ? ` · answer: ${correctLabel}` : ''}
                                        </span>
                                        <Badge variant={pts > 0 ? 'default' : 'secondary'} className={`text-xs px-1.5 py-0 flex-shrink-0 ${pts > 0 ? 'bg-green-500 text-white' : ''}`}>
                                            {pts > 0 ? `+${pts}` : '0'}
                                        </Badge>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardContent>
        </Card>
        </>
    );
}

// ── Scoring Info ──────────────────────────────────────────────────────────────

function ScoringInfo({ hasKnockout = false }) {
    const [open, setOpen] = useState(false);
    return (
        <Card className="mb-4">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium"
            >
                <span className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    How Scoring Works
                </span>
                {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {open && (
                <CardContent className="pt-0 pb-4 space-y-3 text-sm text-muted-foreground">
                    <div className="space-y-1">
                        <div className="flex justify-between items-center">
                            <span className="font-medium text-foreground">Correct result</span>
                            <Badge variant="outline">2 pts</Badge>
                        </div>
                        <p className="text-xs">You predicted the right outcome — home win, away win, or draw — but not the exact scoreline.</p>
                    </div>
                    <div className="space-y-1">
                        <div className="flex justify-between items-center">
                            <span className="font-medium text-foreground">Exact score</span>
                            <Badge variant="outline">5 pts</Badge>
                        </div>
                        <p className="text-xs">You predicted the precise final score, e.g. 2–1. This includes the correct result points — so an exact score is always worth 5 pts total.</p>
                    </div>
                    {/* 90-minute rule — always shown, extra emphasis for knockout comps */}
                    <div className={`rounded-lg p-3 text-xs leading-relaxed ${hasKnockout ? 'bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400' : 'bg-muted/60 text-muted-foreground'}`}>
                        <span className="font-semibold">⏱️ 90-minute rule:</span> all predictions are
                        judged on the score at <strong>full time (90 mins)</strong>.
                        {hasKnockout && (
                            <> If a knockout match goes to extra time or a penalty shoot-out, the
                            score at the end of 90 minutes is what counts — <strong>goals in extra
                            time and penalties do not carry over</strong>.</>
                        )}
                    </div>
                </CardContent>
            )}
        </Card>
    );
}

// ── Leaderboard Panel ─────────────────────────────────────────────────────────

function PickMatchCard({ match, pick, bonusQuestions = [] }) {
    const isRevealed = match.revealed;
    const isFinished = match.status === 'finished';
    const isLive = match.status === 'live';

    const pts = pick?.points_awarded;
    const hasPick = pick != null;
    let borderColor = 'border-border';
    let ptsColor = '';

    if (isRevealed && hasPick && pts != null) {
        if (pts >= 5)      { borderColor = 'border-green-500/60';  ptsColor = 'text-green-400'; }
        else if (pts > 0)  { borderColor = 'border-amber-400/60';  ptsColor = 'text-amber-400'; }
        else               { borderColor = 'border-red-500/30';    ptsColor = 'text-red-400'; }
    }

    // Knockout ties decided in extra time / penalties are scored on the 90-min
    // result, so the board must show that (ft_home_score) — not the after-ET
    // score. Group games have no ET, where ft_ equals the final score, so this
    // falls back safely to home_score/away_score when ft_ isn't captured.
    const actualHome = match.ft_home_score != null ? match.ft_home_score : match.home_score;
    const actualAway = match.ft_away_score != null ? match.ft_away_score : match.away_score;

    // Rich tooltip content — rendered via Radix Portal so it escapes overflow containers
    const tooltipBody = (
        <div className="space-y-2 min-w-[170px]">
            {/* Team names */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-1.5 min-w-0">
                    <TeamBadge teamId={match.home_team_id} teamName={match.home_team_name} size="sm" />
                    <span className="font-semibold leading-tight">{match.home_team_name}</span>
                </div>
                <span className="text-muted-foreground flex-shrink-0 pt-0.5">vs</span>
                <div className="flex items-center gap-1.5 min-w-0 justify-end">
                    <span className="font-semibold leading-tight text-right">{match.away_team_name}</span>
                    <TeamBadge teamId={match.away_team_id} teamName={match.away_team_name} size="sm" />
                </div>
            </div>
            <div className="border-t border-border/50 pt-2 space-y-1">
                {/* Predicted score */}
                {isRevealed ? (
                    hasPick ? (
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Predicted</span>
                            <span className="font-bold tabular-nums">{pick.predicted_home}–{pick.predicted_away}</span>
                        </div>
                    ) : (
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Predicted</span>
                            <span className="italic text-muted-foreground">No pick</span>
                        </div>
                    )
                ) : (
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Kicks off</span>
                        <span className="font-medium">{formatKickoff(match.kickoff_time)}</span>
                    </div>
                )}
                {/* Result */}
                {(isFinished || isLive) && actualHome != null && (
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">{isLive ? 'Live' : 'Result'}</span>
                        <span className="font-bold tabular-nums">{actualHome}–{actualAway}</span>
                    </div>
                )}
                {/* Points */}
                {isRevealed && hasPick && pts != null && (
                    <div className="flex justify-between border-t border-border/50 pt-1 mt-1">
                        <span className="text-muted-foreground">Points</span>
                        <span className={`font-bold ${ptsColor}`}>{pts > 0 ? `+${pts}` : pts}</span>
                    </div>
                )}
                {/* Bonus results */}
                {isFinished && hasPick && pick.bonus_breakdown && Object.keys(pick.bonus_breakdown).length > 0 && (
                    <div className="border-t border-border/50 pt-1.5 mt-1 space-y-1.5">
                        {bonusQuestions.map(q => {
                            const bpts = pick.bonus_breakdown?.[q.id] ?? null;
                            const answered = pick.bonus_answers?.[q.id];
                            const correct = pick.correct_bonus_answers?.[q.id];
                            const isClosest = q.scoring === 'closest';
                            const isVoided = correct === 'no_goal' || correct === null || correct === undefined;
                            const opt = (q.options || []).find(o => o.value === answered);
                            const correctOpt = (q.options || []).find(o => o.value === correct);
                            const answeredLabel = opt?.label || answered || '–';
                            const correctLabel = correct === 'no_goal' ? 'no goal scored' : (correctOpt?.label || correct);
                            const showCorrection = !isVoided && correct && correct !== answered && (!isClosest || bpts === 0);
                            if (isVoided && bpts === null) return null;
                            return (
                                <div key={q.id}>
                                    <p className="text-muted-foreground leading-snug">{q.label}</p>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className={bpts > 0 ? 'text-green-500 font-medium' : 'text-muted-foreground'}>
                                            {answeredLabel}{showCorrection ? ` (was ${correctLabel})` : ''}
                                            {isClosest && bpts > 0 && correct && correct !== answered ? ` · answer: ${correctLabel}` : ''}
                                        </span>
                                        <Badge variant={bpts > 0 ? 'default' : 'secondary'}
                                            className={`text-[10px] px-1.5 py-0 flex-shrink-0 ${bpts > 0 ? 'bg-green-500 text-white' : ''}`}>
                                            {bpts > 0 ? `+${bpts}` : '0'}
                                        </Badge>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );

    const cardContent = !isRevealed ? (
        // Hidden / pre-kickoff card
        <div className={`flex-shrink-0 w-[72px] rounded-lg border bg-muted/30 p-2 flex flex-col items-center justify-center gap-1.5 cursor-default`}>
            <div className="flex items-center gap-1">
                <TeamBadge teamId={match.home_team_id} teamName={match.home_team_name} size="sm" />
                <span className="text-[10px] text-muted-foreground">vs</span>
                <TeamBadge teamId={match.away_team_id} teamName={match.away_team_name} size="sm" />
            </div>
            <Lock className="h-3 w-3 text-muted-foreground" />
        </div>
    ) : (
        // Revealed card
        <div className={`flex-shrink-0 w-[72px] rounded-lg border ${borderColor} bg-card p-2 flex flex-col items-center gap-1.5 cursor-default`}>
            <div className="flex items-center gap-1">
                <TeamBadge teamId={match.home_team_id} teamName={match.home_team_name} size="sm" />
                <span className="text-[10px] text-muted-foreground">vs</span>
                <TeamBadge teamId={match.away_team_id} teamName={match.away_team_name} size="sm" />
            </div>
            {hasPick ? (
                <p className="text-sm font-bold tabular-nums leading-none">{pick.predicted_home}–{pick.predicted_away}</p>
            ) : (
                <p className="text-[10px] text-muted-foreground italic">–</p>
            )}
            {(isFinished || isLive) && actualHome != null && (
                <div className="text-center border-t border-border/40 pt-1 w-full">
                    <p className="text-xs font-semibold tabular-nums leading-none">{actualHome}–{actualAway}</p>
                    <p className="text-[10px] text-muted-foreground">{isLive ? 'Live' : 'FT'}</p>
                </div>
            )}
            {hasPick && pts != null && (
                <p className={`text-xs font-bold leading-none ${ptsColor}`}>{pts > 0 ? `+${pts}` : pts}</p>
            )}
        </div>
    );

    return (
        <Tooltip>
            <TooltipTrigger asChild>{cardContent}</TooltipTrigger>
            <TooltipContent
                side="top"
                className="bg-card text-foreground border border-border shadow-xl text-xs p-3 rounded-xl"
            >
                {tooltipBody}
            </TooltipContent>
        </Tooltip>
    );
}

function PicksBoard({ picksData, loading, currentUserId, leaderboard, advancementCount, bonusQuestions = [] }) {
    const [expanded, setExpanded] = useState(new Set());
    const [showAll, setShowAll] = useState(false);
    const LIMIT = 25;

    const toggle = (uid) => setExpanded(prev => {
        const next = new Set(prev);
        next.has(uid) ? next.delete(uid) : next.add(uid);
        return next;
    });

    if (loading) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Eye className="h-4 w-4 text-primary" />
                        Picks Board
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 p-4 pt-0">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                </CardContent>
            </Card>
        );
    }

    const matches = picksData?.matches || [];
    const predictionCounts = picksData?.prediction_counts || {};
    const revealedCount = matches.filter(m => m.revealed).length;
    // Denominator for "X/Y picks" — the number of games in the selected round
    // (or all round-assigned games when no round is filtered). picksData is
    // already round-scoped by the endpoint via the active round.
    const roundGames = matches.length;

    if (leaderboard.length === 0) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Eye className="h-4 w-4 text-primary" />
                        Picks Board
                    </CardTitle>
                </CardHeader>
                <CardContent className="py-8 text-center text-muted-foreground text-sm">
                    <Users className="h-6 w-6 mx-auto mb-2 opacity-30" />
                    No players in this competition yet.
                </CardContent>
            </Card>
        );
    }

    // Build pick lookup: userId -> matchId -> pick
    const picksByUser = {};
    for (const match of matches) {
        for (const pick of (match.picks || [])) {
            if (!picksByUser[pick.user_id]) picksByUser[pick.user_id] = {};
            picksByUser[pick.user_id][match.pa_match_id] = { ...pick, match };
        }
    }

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                    <Eye className="h-4 w-4 text-primary" />
                    Picks Board
                    <span className="text-xs font-normal text-muted-foreground ml-auto">
                        {revealedCount}/{matches.length} matches revealed
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
                {(showAll ? leaderboard : leaderboard.slice(0, LIMIT)).map((entry) => {
                    const idx = leaderboard.indexOf(entry);
                    const isMe = entry.user_id === currentUserId;
                    const isExpanded = expanded.has(entry.user_id);
                    const userPicks = picksByUser[entry.user_id] || {};
                    const pickCount = predictionCounts[entry.user_id] || 0;
                    const prevEliminated = idx > 0 && leaderboard[idx - 1].eliminated;
                    const showDivider = entry.eliminated && !prevEliminated && idx > 0;
                    const activeRank = leaderboard.slice(0, idx).filter(e => !e.eliminated).length + 1;
                    const projectedOut = advancementCount && !entry.eliminated && activeRank > advancementCount;
                    const showRelLine = advancementCount && !entry.eliminated && activeRank === advancementCount + 1;

                    return (
                        <div key={entry.user_id}>
                            {showRelLine && (
                                <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border-y border-red-500/30">
                                    <ShieldOff className="h-3 w-3 text-red-500 flex-shrink-0" />
                                    <span className="text-[10px] font-semibold text-red-500 uppercase tracking-widest">Relegation line · top {advancementCount} advance</span>
                                </div>
                            )}
                            {showDivider && (
                                <div className="flex items-center gap-2 px-4 py-2 bg-muted/20 border-y border-border/50">
                                    <ShieldOff className="h-3 w-3 text-destructive/50 flex-shrink-0" />
                                    <span className="text-[10px] font-semibold text-destructive/50 uppercase tracking-widest">Eliminated</span>
                                </div>
                            )}
                        <div className={`border-b last:border-0 ${entry.eliminated ? 'opacity-60' : ''} ${projectedOut ? 'bg-red-500/10' : ''}`}>
                            <button
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors ${isMe ? 'bg-primary/5' : ''}`}
                                onClick={() => toggle(entry.user_id)}
                            >
                                <RankBadge rank={entry.eliminated ? null : activeRank} />
                                <Avatar className="h-7 w-7 flex-shrink-0">
                                    <AvatarFallback className="text-xs">{initials(entry.username)}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <p className={`text-sm font-medium truncate ${isMe ? 'text-primary' : ''}`}>
                                            {isMe ? `${entry.username} (you)` : entry.username}
                                        </p>
                                        {entry.eliminated && (
                                            <span className="text-[10px] font-semibold text-destructive/70 bg-destructive/10 px-1.5 py-0.5 rounded flex-shrink-0">OUT</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        <span className={pickCount < roundGames ? 'text-amber-400 font-medium' : 'text-green-500 font-medium'}>
                                            {pickCount}/{roundGames} picks
                                        </span>
                                        {' · '}{entry.exact_scores} exact · {entry.correct_results} correct
                                    </p>
                                </div>
                                <span className="font-bold text-sm tabular-nums mr-2">{entry.total_points} pts</span>
                                {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                            </button>

                            {isExpanded && (
                                <div className="px-4 pb-4 overflow-x-auto">
                                    {matches.length === 0 ? (
                                        <p className="text-sm text-muted-foreground py-2">No matches in this round.</p>
                                    ) : (
                                        <div className="flex gap-2 pt-1" style={{ minWidth: 'max-content' }}>
                                            {matches.map(match => (
                                                <PickMatchCard
                                                    key={match.pa_match_id}
                                                    match={match}
                                                    pick={userPicks[match.pa_match_id] || null}
                                                    bonusQuestions={bonusQuestions}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {revealedCount === 0 && (
                                        <p className="text-xs text-muted-foreground pt-2">
                                            Picks are hidden until each match kicks off.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        </div>
                    );
                })}

                {/* Show all / show less toggle */}
                {leaderboard.length > LIMIT && (
                    <button
                        onClick={() => setShowAll(v => !v)}
                        className="w-full py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors border-t border-border/50"
                    >
                        {showAll ? `Show less ↑` : `Show all ${leaderboard.length} players ↓`}
                    </button>
                )}
            </CardContent>
        </Card>
    );
}

function LeaderboardPanel({ leaderboard, loading, currentUserId, roundName, advancementCount }) {
    // Hooks must come before any early returns
    const LIMIT = 25;
    const [showAll, setShowAll] = useState(false);

    if (loading) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-amber-400" />
                        Leaderboard
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 p-4 pt-0">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </CardContent>
            </Card>
        );
    }

    if (leaderboard.length === 0) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-amber-400" />
                        Leaderboard
                    </CardTitle>
                </CardHeader>
                <CardContent className="py-8 text-center text-muted-foreground text-sm">
                    <Star className="h-6 w-6 mx-auto mb-2 opacity-30" />
                    No scores yet — predictions settle once matches finish.
                </CardContent>
            </Card>
        );
    }
    const activeCount = leaderboard.filter(e => !e.eliminated).length;

    // Visible slice — always include the current user even if they're outside top 25
    const visibleEntries = showAll ? leaderboard : leaderboard.slice(0, LIMIT);
    const myIdx = leaderboard.findIndex(e => e.user_id === currentUserId);
    const myEntryPinned = !showAll && myIdx >= LIMIT ? leaderboard[myIdx] : null;
    const hidden = leaderboard.length - LIMIT;

    const renderRow = (entry) => {
        const idx = leaderboard.indexOf(entry);
        const isMe = entry.user_id === currentUserId;
        const prevEliminated = idx > 0 && leaderboard[idx - 1].eliminated;
        const showDivider = entry.eliminated && !prevEliminated && idx > 0;
        const activeRank = leaderboard.slice(0, idx).filter(e => !e.eliminated).length + 1;
        // Projected relegation: below the round's "Top N advance" line (and not already out)
        const projectedOut = advancementCount && !entry.eliminated && activeRank > advancementCount;
        const showRelLine = advancementCount && !entry.eliminated && activeRank === advancementCount + 1;
        return (
            <div key={entry.user_id}>
                {showRelLine && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border-y border-red-500/30">
                        <ShieldOff className="h-3 w-3 text-red-500 flex-shrink-0" />
                        <span className="text-[10px] font-semibold text-red-500 uppercase tracking-widest">Relegation line · top {advancementCount} advance</span>
                    </div>
                )}
                {showDivider && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-muted/20 border-y border-border/50">
                        <ShieldOff className="h-3 w-3 text-destructive/50 flex-shrink-0" />
                        <span className="text-[10px] font-semibold text-destructive/50 uppercase tracking-widest">Eliminated</span>
                    </div>
                )}
                <div className={`flex items-center gap-3 px-4 py-3 border-b last:border-0 ${isMe ? 'bg-primary/5' : ''} ${entry.eliminated ? 'opacity-50' : ''} ${projectedOut ? 'bg-red-500/10' : ''}`}>
                    <RankBadge rank={entry.eliminated ? null : activeRank} />
                    <Avatar className="h-7 w-7 flex-shrink-0">
                        <AvatarFallback className="text-xs">{initials(entry.username)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                            <p className={`text-sm font-medium truncate ${isMe ? 'text-primary' : ''}`}>
                                {isMe ? `${entry.username} (you)` : entry.username}
                            </p>
                            {entry.eliminated && (
                                <span className="text-[10px] font-semibold text-destructive/70 bg-destructive/10 px-1.5 py-0.5 rounded flex-shrink-0">OUT</span>
                            )}
                            {entry.tiebreak_applied && !entry.eliminated && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="flex-shrink-0 text-amber-400 cursor-default">
                                            <Scale className="h-3 w-3" />
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="bg-card text-foreground border border-border shadow-xl text-xs p-2 rounded-lg z-50 max-w-[200px]">
                                        Level on this round — ranked by overall tournament points ({entry.overall_points} pts).
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {entry.exact_scores} exact · {entry.correct_results} correct
                        </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                        <p className="font-bold text-sm tabular-nums">{entry.total_points}</p>
                        <p className="text-xs text-muted-foreground">pts</p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-amber-400" />
                    {roundName ? roundName : 'Overall'}
                    <span className="text-xs font-normal text-muted-foreground ml-auto">
                        {activeCount} players
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
                {visibleEntries.map(renderRow)}

                {/* Pinned current-user row when they're outside the visible top 25 */}
                {myEntryPinned && (
                    <>
                        <div className="px-4 py-1.5 text-center text-[11px] text-muted-foreground border-b border-dashed border-border/40">
                            · · · {hidden - 1} more players · · ·
                        </div>
                        {renderRow(myEntryPinned)}
                    </>
                )}

                {/* Show all / show less toggle */}
                {leaderboard.length > LIMIT && (
                    <button
                        onClick={() => setShowAll(v => !v)}
                        className="w-full py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors border-t border-border/50"
                    >
                        {showAll ? `Show less ↑` : `Show all ${leaderboard.length} players ↓`}
                    </button>
                )}
            </CardContent>
        </Card>
    );
}

// ── Tournament Final Reveal ───────────────────────────────────────────────────

function FinalTeamBadge({ teamId, teamName }) {
    const [err, setErr] = useState(false);
    if (!teamId || err) return (
        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
            <span className="text-lg font-bold text-white/60">{(teamName || '?')[0]}</span>
        </div>
    );
    return <img src={badgeUrl(teamId)} alt={teamName} className="w-12 h-12 object-contain" onError={() => setErr(true)} />;
}

function TournamentFinalReveal({ data }) {
    const {
        winner, runner_up,
        final_match, final_matches = [], winner_predictions = {},
        predictions, round_name, points_label = 'pts', bonus_questions = [],
        tiebreak_label = null,
    } = data;
    if (!winner) return null;

    // Multi-match round: use final_matches array; single-match: fall back to final_match
    const matches = final_matches.length > 0 ? final_matches : (final_match ? [final_match] : []);
    const isMultiMatch = matches.length > 1;

    // Legacy single-match winner pred (bonus questions etc.)
    const winnerPred = predictions?.[winner.user_id];
    const runnerPred = predictions?.[runner_up?.user_id];

    return (
        <div className="rounded-2xl overflow-hidden border border-amber-500/30 shadow-2xl mb-6">
            {/* ── Hero ── */}
            <div className="bg-gradient-to-br from-[#0a0510] via-[#1a0a2e] to-[#0f0a20] px-6 pt-8 pb-6 text-center relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl" />
                </div>

                <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.2em] mb-1">{round_name || 'Final'}</p>
                <p className="text-white/50 text-xs mb-6">Tournament Complete</p>

                {/* Winner only */}
                <div className="flex items-end justify-center relative z-10">
                    <div className="flex flex-col items-center gap-2">
                        <div className="text-4xl mb-1">🏆</div>
                        <Avatar className="w-20 h-20 border-4 border-amber-400 shadow-2xl ring-4 ring-amber-400/20">
                            <AvatarFallback className="bg-amber-900 text-amber-100 font-bold text-2xl">
                                {initials(winner.username)}
                            </AvatarFallback>
                        </Avatar>
                        <div className="text-center">
                            <p className="text-amber-300 text-base font-bold truncate max-w-[120px]">{winner.username}</p>
                            <p className="text-amber-400/80 text-xs font-semibold">{winner.total_points} {points_label}</p>
                        </div>
                        <div className="w-20 h-16 rounded-t-lg bg-amber-500/30 border border-amber-500/40 flex items-center justify-center">
                            <Crown className="h-6 w-6 text-amber-400" />
                        </div>
                    </div>
                </div>

                {/* Winner stats row */}
                {(winner.predictions_made > 0) && (
                    <div className="flex items-center justify-center gap-4 mt-5 relative z-10">
                        <div className="text-center">
                            <p className="text-white font-bold text-lg leading-none">{winner.predictions_made}</p>
                            <p className="text-white/40 text-[10px] uppercase tracking-wide mt-0.5">Picks</p>
                        </div>
                        <div className="w-px h-6 bg-white/15" />
                        <div className="text-center">
                            <p className="text-amber-300 font-bold text-lg leading-none">{winner.exact_scores}</p>
                            <p className="text-white/40 text-[10px] uppercase tracking-wide mt-0.5">Exact</p>
                        </div>
                        <div className="w-px h-6 bg-white/15" />
                        <div className="text-center">
                            <p className="text-green-400 font-bold text-lg leading-none">{winner.correct_results}</p>
                            <p className="text-white/40 text-[10px] uppercase tracking-wide mt-0.5">Correct</p>
                        </div>
                    </div>
                )}

                {/* Tiebreak explainer — ONLY shown when the finalists finished level
                    on points and a tiebreak rule decided the champion */}
                {tiebreak_label && (
                    <div className="flex items-center justify-center gap-2 mt-4 relative z-10">
                        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold
                                         bg-amber-500/10 border border-amber-500/40 text-amber-300">
                            <Scale className="h-3.5 w-3.5" />
                            Level on points{runner_up ? ` with ${runner_up.username}` : ''} — champion by {tiebreak_label}
                        </span>
                    </div>
                )}
            </div>

            {/* ── Winner's predictions (multi-match) — tile grid ── */}
            {isMultiMatch && matches.length > 0 && (
                <div className="bg-card border-t border-border/60 px-4 py-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center mb-3">
                        {winner.username}'s Predictions
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {matches.map(m => {
                            const pred = winner_predictions?.[m.pa_match_id];
                            const hasResult = m.home_score != null && m.away_score != null;
                            const pts = pred?.points_awarded ?? null;
                            const isExact = pts != null && pts >= 5;
                            const isCorrect = pts != null && pts > 0 && !isExact;
                            const shortName = n => n ? n.split(' ').pop() : '';
                            return (
                                <div key={m.pa_match_id}
                                    className={`rounded-xl p-2.5 text-center border flex flex-col items-center gap-1.5
                                        ${isExact   ? 'border-green-500/50 bg-green-500/5'
                                        : isCorrect  ? 'border-amber-400/50 bg-amber-500/5'
                                        : pts === 0  ? 'border-red-500/30 bg-red-500/5'
                                        : 'border-border/40 bg-muted/20'}`}>

                                    {/* Badges */}
                                    <div className="flex items-center justify-center gap-2">
                                        <FinalTeamBadge teamId={m.home_team_id} teamName={m.home_team_name} />
                                        <span className="text-[10px] text-muted-foreground/50 font-bold">v</span>
                                        <FinalTeamBadge teamId={m.away_team_id} teamName={m.away_team_name} />
                                    </div>

                                    {/* Team names */}
                                    <p className="text-[10px] text-muted-foreground leading-tight truncate w-full text-center">
                                        {shortName(m.home_team_name)} v {shortName(m.away_team_name)}
                                    </p>

                                    {/* Actual result */}
                                    {hasResult ? (
                                        <p className="text-base font-black tabular-nums text-foreground leading-none">
                                            {m.home_score} – {m.away_score}
                                        </p>
                                    ) : (
                                        <p className="text-base font-black text-muted-foreground/30 leading-none">– –</p>
                                    )}

                                    {/* Prediction + points */}
                                    {pred ? (
                                        <div className={`text-[11px] font-semibold tabular-nums leading-none px-2 py-0.5 rounded-full
                                            ${isExact   ? 'text-green-400 bg-green-500/15'
                                            : isCorrect  ? 'text-amber-300 bg-amber-500/15'
                                            : 'text-red-400/70 bg-red-500/10'}`}>
                                            {pred.home_score} – {pred.away_score}
                                            {pts != null && <span className="ml-1 opacity-70">+{pts}</span>}
                                        </div>
                                    ) : (
                                        <p className="text-[10px] text-muted-foreground/30">no pick</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Single-match final: original detailed view ── */}
            {!isMultiMatch && final_match && (winnerPred || runnerPred) && (
                <div className="bg-card border-t border-border/60 px-4 py-5">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center mb-4">Final Match Predictions</p>

                    <div className="flex items-center justify-center gap-3 mb-4 pb-4 border-b border-border/40">
                        <div className="flex items-center gap-2">
                            <FinalTeamBadge teamId={final_match.home_team_id} teamName={final_match.home_team_name} />
                            <span className="text-sm font-semibold">{final_match.home_team_name}</span>
                        </div>
                        <div className="text-center px-3">
                            {final_match.home_score != null ? (
                                <span className="text-2xl font-black text-foreground tabular-nums">
                                    {final_match.home_score} – {final_match.away_score}
                                </span>
                            ) : (
                                <span className="text-muted-foreground text-sm">vs</span>
                            )}
                            <p className="text-[10px] text-muted-foreground mt-0.5">Result</p>
                        </div>
                        <div className="flex items-center gap-2 flex-row-reverse">
                            <FinalTeamBadge teamId={final_match.away_team_id} teamName={final_match.away_team_name} />
                            <span className="text-sm font-semibold">{final_match.away_team_name}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                        {[
                            { label: winner.username, pred: winnerPred, isWinner: true },
                            { label: runner_up?.username, pred: runnerPred, isWinner: false },
                        ].map(({ label, pred, isWinner }) => label && pred && (
                            <div key={label}
                                className={`rounded-xl p-3 text-center border ${isWinner ? 'border-amber-500/40 bg-amber-500/5' : 'border-border/60 bg-muted/20'}`}>
                                <div className="flex items-center justify-center gap-1.5 mb-2">
                                    {isWinner ? <Crown className="h-3.5 w-3.5 text-amber-400" /> : <Medal className="h-3.5 w-3.5 text-slate-400" />}
                                    <p className={`text-xs font-bold ${isWinner ? 'text-amber-300' : 'text-muted-foreground'}`}>{label}</p>
                                </div>
                                <p className="text-xs text-muted-foreground mb-1">Predicted</p>
                                <p className="text-2xl font-black tabular-nums text-foreground">
                                    {pred.home_score != null && pred.away_score != null
                                        ? `${pred.home_score} – ${pred.away_score}`
                                        : '? – ?'}
                                </p>
                                {pred.points_awarded != null && (
                                    <p className={`text-xs font-semibold mt-1 ${pred.points_awarded > 0 ? 'text-green-400' : 'text-muted-foreground'}`}>
                                        +{pred.points_awarded} pts total
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>

                    {bonus_questions.length > 0 && (winnerPred || runnerPred) && (
                        <div className="rounded-xl border border-border/60 overflow-hidden">
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide px-3 py-2 bg-muted/20 border-b border-border/40">
                                Bonus Results
                            </p>
                            {bonus_questions.map(q => {
                                const isClosest = q.scoring === 'closest';
                                const renderSide = (pred) => {
                                    if (!pred) return <span className="text-muted-foreground/40">–</span>;
                                    const pts = pred.bonus_breakdown?.[q.id] ?? null;
                                    const answered = pred.bonus_answers?.[q.id];
                                    const correct = pred.correct_bonus_answers?.[q.id];
                                    const isVoided = correct === 'no_goal' || correct === null || correct === undefined;
                                    const opt = (q.options || []).find(o => o.value === answered);
                                    const correctOpt = (q.options || []).find(o => o.value === correct);
                                    const label = opt?.label || answered || '–';
                                    const correctLabel = correct === 'no_goal' ? 'no goal scored' : (correctOpt?.label || correct);
                                    // For closest questions: only show "was X" if this side scored 0 (not closest)
                                    const showCorrection = !isVoided && correct && correct !== answered && (!isClosest || pts === 0);
                                    return (
                                        <div className="flex items-center gap-1.5 justify-end">
                                            <span className={`text-xs ${pts > 0 ? 'text-green-400 font-medium' : 'text-muted-foreground'}`}>
                                                {label}{showCorrection ? ` (was ${correctLabel})` : ''}
                                                {isClosest && pts > 0 && correct && correct !== answered ? ` · answer: ${correctLabel}` : ''}
                                            </span>
                                            <Badge variant={pts > 0 ? 'default' : 'secondary'}
                                                className={`text-[10px] px-1.5 py-0 flex-shrink-0 ${pts > 0 ? 'bg-green-500 text-white' : ''}`}>
                                                {pts > 0 ? `+${pts}` : '0'}
                                            </Badge>
                                        </div>
                                    );
                                };
                                return (
                                    <div key={q.id} className="px-3 py-2 border-b border-border/30 last:border-0">
                                        <p className="text-[11px] text-muted-foreground/70 text-center leading-snug mb-1.5">{q.label}</p>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="flex justify-center">{renderSide(winnerPred)}</div>
                                            <div className="flex justify-center">{renderSide(runnerPred)}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Share / Invite Modal ──────────────────────────────────────────────────────

function ShareModal({ open, onClose, comp }) {
    const [copied, setCopied] = useState(false);
    const inviteUrl = comp?.invite_code
        ? `${window.location.origin}/football/join/${comp.invite_code}`
        : null;

    function copyLink() {
        if (!inviteUrl) return;
        navigator.clipboard.writeText(inviteUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="w-[calc(100vw-2rem)] max-w-md p-0 overflow-hidden">
                {/* Header — matches join page card style */}
                <div className="bg-gradient-to-b from-primary/10 to-transparent px-6 pt-8 pb-5 text-center border-b border-border/60">
                    <div className="mx-auto w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mb-3">
                        <Trophy className="h-7 w-7 text-primary" />
                    </div>
                    <h2 className="text-xl font-bold font-heading leading-tight">{comp?.name}</h2>
                    {comp?.description && (
                        <p className="text-sm text-muted-foreground mt-1">{comp.description}</p>
                    )}
                    <div className="flex justify-center gap-6 mt-4 text-sm">
                        <div className="text-center">
                            <p className="text-muted-foreground text-xs">Entries</p>
                            <p className="font-semibold">{comp?.entry_count ?? 0}</p>
                        </div>
                        {comp?.start_date && (
                            <div className="text-center">
                                <p className="text-muted-foreground text-xs">From</p>
                                <p className="font-semibold">{formatCompDate(comp.start_date)}</p>
                            </div>
                        )}
                        {comp?.end_date && (
                            <div className="text-center">
                                <p className="text-muted-foreground text-xs">To</p>
                                <p className="font-semibold">{formatCompDate(comp.end_date)}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* QR + copy section */}
                <div className="px-4 py-5 space-y-4">
                    {inviteUrl ? (
                        <>
                            <div className="flex justify-center p-3 bg-white rounded-xl">
                                <QRCodeSVG value={inviteUrl} size={160} />
                            </div>
                            <p className="text-xs text-center text-muted-foreground">Scan to join, or share the link below</p>
                            <div className="space-y-2">
                                <div className="text-xs text-muted-foreground bg-muted rounded-md px-3 py-2 font-mono truncate w-full">
                                    {inviteUrl}
                                </div>
                                <Button variant="outline" onClick={copyLink} className="w-full gap-2">
                                    {copied ? <><Check className="h-4 w-4 text-green-500" />Copied!</> : <><Copy className="h-4 w-4" />Copy invite link</>}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-muted-foreground text-center py-2">No invite code available.</p>
                    )}
                    <Button variant="outline" className="w-full" onClick={onClose}>Close</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function FootballCompetitionDetailPage() {
    const { compId } = useParams();
    const { user } = useAuth();
    const [comp, setComp] = useState(null);
    const [isJoined, setIsJoined] = useState(false);
    const [isEliminated, setIsEliminated] = useState(false);
    const [eliminatedRoundId, setEliminatedRoundId] = useState(null);
    const [joining, setJoining] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [matches, setMatches] = useState([]);
    const [allMatches, setAllMatches] = useState([]);
    const [rounds, setRounds] = useState([]);
    const [activeRoundId, setActiveRoundId] = useState(null); // null = show all
    const [predictions, setPredictions] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [leaderboardLoading, setLeaderboardLoading] = useState(true);
    const [activeView, setActiveView] = useState('predictions'); // 'predictions' | 'picks'
    const [picksData, setPicksData] = useState(null);
    const [picksLoading, setPicksLoading] = useState(false);
    const [bonusQuestions, setBonusQuestions] = useState([]); // questions for active round
    const [bonusAnswers, setBonusAnswers] = useState({}); // {matchId: {questionId: answer}}
    const [pendingScores, setPendingScores] = useState({}); // {matchId: {home, away}} — scores entered in steppers but not yet locked
    const [finalReveal, setFinalReveal] = useState(null);

    const fetchPicks = async (roundId = null) => {
        setPicksLoading(true);
        const token = getToken();
        try {
            const params = roundId ? `?round_id=${roundId}` : '';
            const res = await axios.get(`${API_URL}/api/football/competitions/${compId}/picks${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPicksData(res.data);
        } catch {
            // silent
        } finally {
            setPicksLoading(false);
        }
    };

    const fetchLeaderboard = async (roundId = null) => {
        const token = getToken();
        try {
            const params = roundId ? `?round_id=${roundId}` : '';
            const res = await axios.get(`${API_URL}/api/football/competitions/${compId}/leaderboard${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setLeaderboard(res.data.leaderboard || []);
        } catch {
            // silent
        } finally {
            setLeaderboardLoading(false);
        }
    };

    const refreshScores = async () => {
        const token = getToken();
        try {
            const res = await axios.post(
                `${API_URL}/api/football/competitions/${compId}/refresh-scores`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const updated = res.data.matches || [];
            setAllMatches(updated.sort((a, b) => (a.kickoff_time || '').localeCompare(b.kickoff_time || '')));
        } catch {
            // silent — background refresh
        }
    };

    useEffect(() => {
        const token = getToken();

        const load = async () => {
            try {
                const [compRes, predRes, finalRes] = await Promise.all([
                    axios.get(`${API_URL}/api/football/competitions/${compId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                    axios.get(`${API_URL}/api/football/competitions/${compId}/predictions`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                    axios.get(`${API_URL}/api/football/competitions/${compId}/final-reveal`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }).catch(() => ({ data: null })),
                ]);
                if (finalRes.data?.has_final && finalRes.data?.final_complete) {
                    setFinalReveal(finalRes.data);
                }

                const compData = compRes.data.competition;
                setComp(compData);
                setIsJoined(compRes.data.is_joined !== false);
                setIsEliminated(compRes.data.is_eliminated === true);
                setEliminatedRoundId(compRes.data.eliminated_round_id || null);

                const matchList = (compRes.data.matches || [])
                    .sort((a, b) => (a.kickoff_time || '').localeCompare(b.kickoff_time || ''));
                setAllMatches(matchList);
                setMatches(matchList);

                const roundList = compRes.data.rounds || [];
                setRounds(roundList);
                // Default to first active/upcoming round if rounds exist
                if (roundList.length > 0 && activeRoundId === null) {
                    const active = roundList.find(r => r.status === 'active') || roundList[0];
                    setActiveRoundId(active.id);
                }

                const predMap = {};
                (predRes.data.predictions || []).forEach(p => { predMap[p.pa_match_id] = p; });
                setPredictions(predMap);
            } catch {
                toast.error('Failed to load competition');
            } finally {
                setLoading(false);
            }
        };

        load();
        fetchLeaderboard();
    }, [compId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-fetch leaderboard + picks + bonus questions when round tab changes
    useEffect(() => {
        if (!loading) {
            setLeaderboardLoading(true);
            fetchLeaderboard(activeRoundId);
            if (activeView === 'picks') fetchPicks(activeRoundId);
            // Load bonus questions for the selected round
            if (activeRoundId) {
                const token = getToken();
                axios.get(`${API_URL}/api/football/competitions/${compId}/rounds/${activeRoundId}/bonus-questions`, {
                    headers: { Authorization: `Bearer ${token}` },
                }).then(res => setBonusQuestions(res.data.questions || [])).catch(() => setBonusQuestions([]));
            } else {
                setBonusQuestions([]);
            }
        }
    }, [activeRoundId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch picks when switching to the picks view
    useEffect(() => {
        if (activeView === 'picks' && picksData === null && !picksLoading) {
            fetchPicks(activeRoundId);
        }
    }, [activeView]); // eslint-disable-line react-hooks/exhaustive-deps

    // Filter matches by active round
    useEffect(() => {
        if (rounds.length === 0) {
            // No rounds set up — show everything
            setMatches(allMatches);
            return;
        }
        const knockout = rounds.some(r => r.advancement_rule === 'top_n')
            || rounds.some(r => /round of|quarter|semi|final|last \d+|r\d+|rd\d+|qf|sf/i.test(r.name || ''));
        const list = !activeRoundId
            ? allMatches.filter(m => m.round_id)           // "All Matches" — any round
            : allMatches.filter(m => m.round_id === activeRoundId);
        if (!knockout) {
            setMatches(list);
            return;
        }
        // Knockout comps: order by phase (Round of 32 → 16 → Quarter Final → …)
        // latest-first, using each phase's latest kickoff date; chronological
        // within a phase. Grouping on the PA sub-round name (match.round) means
        // this works even when several phases share one competition round
        // (e.g. a single "Knock Out Rounds" round holds R32, R16 and QF).
        const phaseLatest = {};
        list.forEach(m => {
            const key = m.round || '';
            const k = m.kickoff_time || '';
            if (k > (phaseLatest[key] || '')) phaseLatest[key] = k;
        });
        const ordered = [...list].sort((a, b) => {
            const pk = (phaseLatest[b.round || ''] || '').localeCompare(phaseLatest[a.round || ''] || '');
            return pk !== 0 ? pk : (a.kickoff_time || '').localeCompare(b.kickoff_time || '');
        });
        setMatches(ordered);
    }, [allMatches, rounds, activeRoundId]);

    // Auto-poll every 60s when there are live or imminent matches
    useEffect(() => {
        const hasLive = matches.some(m => m.status === 'live');
        if (!hasLive) return;
        const interval = setInterval(refreshScores, 60000);
        return () => clearInterval(interval);
    }, [matches]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleJoin = async () => {
        setJoining(true);
        const token = getToken();
        try {
            await axios.post(
                `${API_URL}/api/football/competitions/${compId}/join`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Joined! Time to make your predictions.');
            setIsJoined(true);
            fetchLeaderboard();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to join competition');
        } finally {
            setJoining(false);
        }
    };

    const [lockAllSubmitting, setLockAllSubmitting] = useState(false);

    const handleLockAll = async (pendingPicks) => {
        setLockAllSubmitting(true);
        const token = getToken();
        const results = await Promise.allSettled(
            pendingPicks.map(async ({ match, prediction: p, home, away }) => {
                const matchId = match.pa_match_id;
                const savedBonus = p?.bonus_answers || {};
                const localBonus = bonusAnswers[matchId] || {};
                const matchBonus = { ...savedBonus, ...localBonus };
                const res = await axios.post(
                    `${API_URL}/api/football/competitions/${compId}/predictions`,
                    {
                        pa_match_id: matchId,
                        predicted_home_score: home,
                        predicted_away_score: away,
                        confirm_lock: true,
                        bonus_answers: matchBonus,
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                return { matchId, homeScore: home, awayScore: away, locked: res.data.locked ?? true };
            })
        );
        let successCount = 0;
        results.forEach(r => {
            if (r.status === 'fulfilled') {
                successCount++;
                const { matchId, homeScore, awayScore, locked } = r.value;
                setPredictions(prev => ({
                    ...prev,
                    [matchId]: {
                        ...prev[matchId],
                        pa_match_id: matchId,
                        predicted_home_score: homeScore,
                        predicted_away_score: awayScore,
                        locked,
                    },
                }));
                // Remove from pending scores so the bar count drops
                setPendingScores(prev => {
                    const next = { ...prev };
                    delete next[matchId];
                    return next;
                });
            }
        });
        const failCount = results.filter(r => r.status === 'rejected').length;
        if (successCount > 0) toast.success(`${successCount} prediction${successCount !== 1 ? 's' : ''} locked in!`);
        if (failCount > 0) toast.error(`${failCount} prediction${failCount !== 1 ? 's' : ''} failed to lock`);
        setLockAllSubmitting(false);
    };

    const handleSubmit = async (matchId, homeScore, awayScore, confirmLock = false) => {
        // Merge previously-saved bonus answers with any local changes
        const savedBonus = predictions[matchId]?.bonus_answers || {};
        const localBonus = bonusAnswers[matchId] || {};
        const matchBonus = { ...savedBonus, ...localBonus };

        // Block submission if any bonus question is unanswered
        if (bonusQuestions.length > 0) {
            const unanswered = bonusQuestions.filter(q => {
                const val = matchBonus[q.id];
                return val === undefined || val === null || val === '';
            });
            if (unanswered.length > 0) {
                toast.error(`Please answer all bonus questions before locking in.`);
                return;
            }
        }

        setSubmitting(matchId);
        const token = getToken();
        try {
            const res = await axios.post(
                `${API_URL}/api/football/competitions/${compId}/predictions`,
                {
                    pa_match_id: matchId,
                    predicted_home_score: homeScore,
                    predicted_away_score: awayScore,
                    confirm_lock: confirmLock,
                    bonus_answers: matchBonus,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPredictions(prev => ({
                ...prev,
                [matchId]: {
                    ...prev[matchId],
                    pa_match_id: matchId,
                    predicted_home_score: homeScore,
                    predicted_away_score: awayScore,
                    bonus_answers: matchBonus,
                    locked: res.data.locked ?? prev[matchId]?.locked ?? false,
                },
            }));
            toast.success(confirmLock ? 'Prediction locked in!' : 'Prediction saved!');
            // If locked individually, remove from pending so the bar count drops
            if (confirmLock) {
                setPendingScores(prev => {
                    const next = { ...prev };
                    delete next[matchId];
                    return next;
                });
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save prediction');
        } finally {
            setSubmitting(null);
        }
    };

    const setBonusAnswer = (matchId, questionId, value) => {
        setBonusAnswers(prev => ({
            ...prev,
            [matchId]: { ...(prev[matchId] || {}), [questionId]: value },
        }));
    };

    const predCount = matches.filter(m => predictions[m.pa_match_id]).length;
    const userEntry = leaderboard.find(e => e.user_id === user?.id);
    const hasLive = matches.some(m => m.status === 'live');

    // ── Loading skeleton ───────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="container-app py-8">
                <Skeleton className="h-10 w-1/3 mb-2" />
                <Skeleton className="h-5 w-1/4 mb-8" />
                <div className="grid lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <Skeleton className="h-96" />
                    </div>
                    <Skeleton className="h-96" />
                </div>
            </div>
        );
    }

    // ── Join gate ──────────────────────────────────────────────────────────────
    if (!isJoined) {
        return (
            <div className="container-app py-8">
                <div className="flex items-center gap-3 mb-8">
                    <Link to="/football">
                        <Button variant="ghost" size="icon">
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                    </Link>
                    <div>
                        <h1 className="text-3xl font-bold font-heading page-title">{comp?.name}</h1>
                        {comp?.description && (
                            <p className="text-sm text-muted-foreground mt-1">{comp.description}</p>
                        )}
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <Card className="border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-transparent">
                            <CardContent className="py-10 px-6 flex flex-col items-center text-center gap-4">
                                <Trophy className="h-12 w-12 text-amber-500 opacity-80" />
                                <div>
                                    <h2 className="text-xl font-bold font-heading mb-1">{comp?.name}</h2>
                                    {comp?.description && (
                                        <p className="text-sm text-muted-foreground mb-3">{comp.description}</p>
                                    )}
                                    {comp?.entry_count != null && (
                                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1 mb-4">
                                            <Users className="h-3.5 w-3.5" />
                                            {comp.entry_count} {comp.entry_count === 1 ? 'entry' : 'entries'}
                                        </p>
                                    )}
                                </div>
                                <Button
                                    size="lg"
                                    className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8"
                                    onClick={handleJoin}
                                    disabled={joining}
                                >
                                    {joining ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Users className="h-4 w-4 mr-2" />}
                                    Join Competition
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                    <div>
                        <ScoringInfo hasKnockout={rounds.length > 0} />
                        <LeaderboardPanel leaderboard={leaderboard} loading={leaderboardLoading} currentUserId={user?.id} />
                    </div>
                </div>
            </div>
        );
    }

    // ── Last Man Standing: a completely different game/UI, same competition shell ──
    if (comp?.format === 'last_man_standing') {
        return <LastManStandingCompetitionPage compId={compId} comp={comp} isEliminated={isEliminated} />;
    }

    // ── Main view ──────────────────────────────────────────────────────────────
    // Matches where the user has entered scores in the steppers but not yet locked
    const pendingLockPicks = matches
        .filter(m => {
            const ps = pendingScores[m.pa_match_id];
            const alreadyLocked = predictions[m.pa_match_id]?.locked === true;
            return m.status === 'scheduled' && !alreadyLocked && ps !== undefined;
        })
        .map(m => ({
            match: m,
            prediction: predictions[m.pa_match_id] || null,
            home: pendingScores[m.pa_match_id].home,
            away: pendingScores[m.pa_match_id].away,
        }));

    // Projected relegation line for the selected round (only when it eliminates Top N)
    const activeRound = rounds.find(r => r.id === activeRoundId);
    const advancementCount = activeRound?.advancement_rule === 'top_n' ? activeRound.advancement_count : null;

    return (
        <div className={`container-app py-8 ${pendingLockPicks.length >= 2 ? 'pb-24' : ''}`}>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                <div className="flex items-start gap-3">
                    <Link to="/football">
                        <Button variant="ghost" size="icon" className="mt-0.5">
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                    </Link>
                    <div>
                        <h1 className="text-3xl font-bold font-heading page-title">{comp?.name}</h1>
                        {comp?.description && (
                            <p className="text-muted-foreground mt-0.5">{comp.description}</p>
                        )}
                        {comp?.start_date && comp?.end_date && (
                            <p className="text-sm text-muted-foreground mt-1">
                                {formatCompDate(comp.start_date)} – {formatCompDate(comp.end_date)}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 ml-10 md:ml-0">
                    {hasLive && (
                        <span className="flex items-center gap-1.5 text-xs text-green-400 font-medium">
                            <span className="flex h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                            Live
                        </span>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}>
                        <Share2 className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { refreshScores(); fetchLeaderboard(); }}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} comp={comp} />

            {/* Tournament Final Reveal */}
            {finalReveal && <TournamentFinalReveal data={finalReveal} />}

            {/* User stats banner */}
            {userEntry && (
                <Card className="mb-6 bg-gradient-to-r from-primary/10 to-transparent border-primary/20">
                    <CardContent className="py-4">
                        <div className="flex items-center gap-6 flex-wrap">
                            <div>
                                <p className="text-sm text-muted-foreground">Your Rank</p>
                                <span className="text-2xl font-bold font-data">#{userEntry.rank}</span>
                            </div>
                            <div className="h-10 w-px bg-border" />
                            <div>
                                <p className="text-sm text-muted-foreground">Points</p>
                                <span className="text-2xl font-bold font-data">{userEntry.total_points}</span>
                            </div>
                            <div className="h-10 w-px bg-border" />
                            <div>
                                <p className="text-sm text-muted-foreground">Exact Scores</p>
                                <span className="text-2xl font-bold font-data text-amber-400">{userEntry.exact_scores}</span>
                            </div>
                            <div className="h-10 w-px bg-border hidden sm:block" />
                            <div className="hidden sm:block">
                                <p className="text-sm text-muted-foreground">Correct Results</p>
                                <span className="text-2xl font-bold font-data text-green-400">{userEntry.correct_results}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Elimination banner */}
            {isEliminated && (
                <Card className="mb-6 border-destructive/40 bg-destructive/5">
                    <CardContent className="py-4 flex items-center gap-3">
                        <ShieldOff className="h-5 w-5 text-destructive flex-shrink-0" />
                        <div>
                            <p className="font-semibold text-destructive">You have been eliminated</p>
                            <p className="text-sm text-muted-foreground">
                                {(eliminatedRoundId
                                    ? `Eliminated after ${rounds.find(r => r.id === eliminatedRoundId)?.name || 'a previous round'}. `
                                    : '')
                                }You're now spectating — follow the remaining matches, the picks and the leaderboard, but picks are closed for you.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Round navigation tabs */}
            {rounds.length > 0 && (
                <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                    <button
                        onClick={() => setActiveRoundId(null)}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            activeRoundId === null
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        All Matches
                    </button>
                    {rounds.map(r => (
                        <button
                            key={r.id}
                            onClick={() => setActiveRoundId(r.id)}
                            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                activeRoundId === r.id
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            <Flag className="h-3.5 w-3.5" />
                            {r.name}
                            {r.status === 'complete' && (
                                <CheckCircle2 className="h-3 w-3 ml-0.5 opacity-70" />
                            )}
                        </button>
                    ))}
                </div>
            )}

            {/* View toggle */}
            <div className="flex gap-2 mb-4">
                <button
                    onClick={() => setActiveView('predictions')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        activeView === 'predictions'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                >
                    <Target className="h-3.5 w-3.5" />
                    My Predictions
                </button>
                <button
                    onClick={() => setActiveView('picks')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        activeView === 'picks'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                >
                    <Eye className="h-3.5 w-3.5" />
                    Picks Board
                </button>
            </div>

            {/* Two-column layout */}
            <div className="grid lg:grid-cols-3 gap-6">
                {/* Left: predictions or picks board */}
                <div className="lg:col-span-2 space-y-4">
                    {activeView === 'picks' ? (
                        <PicksBoard
                            picksData={picksData}
                            loading={picksLoading}
                            currentUserId={user?.id}
                            leaderboard={leaderboard}
                            advancementCount={advancementCount}
                            bonusQuestions={bonusQuestions}
                        />
                    ) : (
                        <>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                                {predCount} / {matches.length} predicted
                            </span>
                        </div>

                        {matches.length === 0 ? (
                            <Card>
                                <CardContent className="py-10 text-center text-muted-foreground">
                                    <Globe className="h-7 w-7 mx-auto mb-2 opacity-30" />
                                    <p className="font-semibold tracking-widest">MATCHES TO BE ANNOUNCED</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className={matches.length > 1 ? 'grid sm:grid-cols-2 gap-3' : 'space-y-3'}>
                                {matches.map(match => {
                                    // Eliminated players spectate: the card stays fully
                                    // visible (teams, kickoff, bonus questions, live score)
                                    // but read-only — no blur overlay, no inputs.
                                    let lockReason = null;
                                    if (match.status === 'scheduled' && !isEliminated && match.round_id) {
                                        const matchRound = rounds.find(r => r.id === match.round_id);
                                        if (matchRound?.status === 'upcoming') {
                                            lockReason = 'Awaiting previous round results';
                                        }
                                    }
                                    return (
                                    <MatchCard
                                        key={match.pa_match_id}
                                        match={match}
                                        prediction={predictions[match.pa_match_id] || null}
                                        onSubmit={handleSubmit}
                                        submitting={submitting}
                                        lockReason={lockReason}
                                        spectator={isEliminated}
                                        bonusQuestions={bonusQuestions}
                                        bonusAnswers={{...(predictions[match.pa_match_id]?.bonus_answers || {}), ...(bonusAnswers[match.pa_match_id] || {})}}
                                        onBonusAnswer={(qId, val) => setBonusAnswer(match.pa_match_id, qId, val)}
                                        onScoreChange={(matchId, h, a) => setPendingScores(prev => ({ ...prev, [matchId]: { home: h, away: a } }))}
                                    />
                                    );
                                })}
                            </div>
                        )}
                        </>
                    )}
                </div>

                {/* Right: scoring info + leaderboard */}
                <div>
                    <ScoringInfo hasKnockout={rounds.length > 0 || allMatches.some(m => {
                        const r = (m.round || '').toLowerCase();
                        return r.includes('final') || r.includes('semi') || r.includes('quarter') || r.includes('round of');
                    })} />
                    <LeaderboardPanel
                        leaderboard={leaderboard}
                        loading={leaderboardLoading}
                        currentUserId={user?.id}
                        roundName={activeRoundId ? rounds.find(r => r.id === activeRoundId)?.name : null}
                        advancementCount={advancementCount}
                    />
                </div>
            </div>

            <LockAllBar
                pendingPicks={pendingLockPicks}
                onLockAll={handleLockAll}
                submitting={lockAllSubmitting}
            />
        </div>
    );
}
